import React, { useState, useRef, useCallback } from 'react'
import Formsy from 'formsy-react'
import {
  DialogTitle,
  DialogContent,
  Grid,
  Icon,
  useMediaQuery,
  Divider,
  Typography,
} from '@material-ui/core'
import { FormattedMessage, useIntl } from 'react-intl'
import GridAllItem from 'app/components/layout/GridAllItem'
import Grow from '@material-ui/core/Grow'
import ResponsiveDialogAction from 'app/components/dialog/ResponsiveDialogAction'
import TextEditorDraftFormsy from 'app/components/textEditorDraft/TextEditorDraftFormsy'
import { useFormsy } from 'commons/form'
import PrimaryButton from 'app/components/buttons/PrimaryButton'
import SecondaryButton from 'app/components/buttons/SecondaryButton'
import { useTheme } from '@material-ui/core/styles'
import { isEmptyOrNil } from 'libs/empty'
import RichTextEditorFormsy, {
  richTextLengthValiator,
} from 'app/components/richTextEditor/RichTextEditorFormsy'
import clsx from 'clsx'
import ResponsiveDialog, {
  useDialogStyle,
} from 'app/components/dialog/ResponsiveDialog'
import { TITLE_MAX_LENGTH, BODY_MAX_LENGTH } from 'app/constants/compose'
import VoucherStartEndTimeFormsy from 'app/main/notification/voucherStartEndTime/VoucherStartEndTimeFormsy'
import PreviewVoucherFormsy from './PreviewVoucherFormsy'
import { VOUCHER } from 'app/main/notification/CreateNotiLens'
import * as R from 'ramda'
import { NumberFieldFormsy } from '@fuse'
import useCreateVoucher from './useCreateVoucher'
import { makeStyles } from '@material-ui/styles'
import { htmlToTextCount } from 'libs/text'
import { useFetcher } from 'libs/fetcher'
import effects from 'app/effects/effects'
import ConfirmationDialog from 'app/components/ConfirmationDialog'
import CancelButton from 'app/components/buttons/CancelButton'

const MINIMUM_LIMIT_AMOUNT = 1
const MAXIMUM_LIMIT_AMOUNT = 2000000000
const STATUS_FULLY = 2
const STATUS_CANCELED = 3
const STATUS_EXPIRED = 4

const textEditorControls = ['DONOTTRANSLATE', 'textCount']

const useStyles = makeStyles(() => ({
  dialogStyle: {
    '& .MuiDialog-paper': {
      maxWidth: '500px !important',
    },
  },
  dialogSubTitle: {
    paddingLeft: '35px',
    paddingRight: '35px',
  },
}))

const CreateVoucherDialog = (props) => {
  const { open, onClose, initialModel, onCreateSuccess } = props
  const { formatMessage } = useIntl()
  const classesDialog = useStyles()
  const editId = R.prop('id', initialModel)

  const isEditMode = R.compose(R.not, isEmptyOrNil)(editId)
  const minSize = useMediaQuery('(max-width:466px)')

  const [isFormValid, setIsFormValid] = useState(false)
  const [isOpenPreview, setIsOpenPreview] = useState(false)
  const [isClosingDialogWarning, setIsClosingDialogWarning] = useState(false)
  const [template, setTemplate] = useState({})
  const formRef = useRef()
  const disableButton = () => setIsFormValid(false)
  const enableButton = () => setIsFormValid(true)

  const { values: templateList } = useFetcher(
    effects.apis.getVoucherTemplateList,
    [],
  )

  const isStatusInActive = R.includes(R.prop('status', initialModel), [
    STATUS_CANCELED,
    STATUS_EXPIRED,
    STATUS_FULLY,
  ])

  const isAttached = R.prop('isAttached', initialModel)
  const isDisabled = isStatusInActive ? true : isEditMode && isAttached
  const getFormModel = () =>
    !R.isNil(formRef.current) ? formRef.current.getModel() : {}

  const previewVoucherData = getFormModel()

  const { isPristine, onChange } = useFormsy(
    formRef,
    (pristineValue, currentValue) => {
      const serialDescription = R.ifElse(
        R.isNil,
        R.always({}),
        R.prop('description'),
      )

      const descriptionPristine = R.equals(
        serialDescription(pristineValue),
        serialDescription(currentValue),
      )

      return (
        descriptionPristine &&
        R.equals(
          R.dissoc('description', pristineValue),
          R.dissoc('description', currentValue),
        )
      )
    },
  )

  const onFormChange = (model) => {
    onChange(model)
  }
  const callSubmit = () => {
    if (!R.isNil(formRef.current)) formRef.current.submit()
  }

  const handlerOnCreateSuccess = useCallback(
    (successModel) => {
      onCreateSuccess(successModel)
    },
    [onCreateSuccess],
  )

  const { submitVoucher, isSubmitting } = useCreateVoucher(
    handlerOnCreateSuccess,
    editId,
  )

  const isFormDisable = () => {
    return !isFormValid || isSubmitting || isPristine
  }

  const onOpenPreview = () => {
    setIsOpenPreview(true)
  }

  const onClosePreview = () => {
    setIsOpenPreview(false)
  }

  const closePreviewThenSubmit = () => {
    setIsOpenPreview(false)
    callSubmit()
  }

  const closeDialog = () => {
    setIsClosingDialogWarning(false)
    formRef.current.reset()
    onClose()
  }

  const onClosingDialog = () => {
    if (!isPristine) {
      setIsClosingDialogWarning(true)
    } else {
      closeDialog()
    }
  }

  const cancelClosingDialogWarning = () => {
    setIsClosingDialogWarning(false)
  }

  // Responsive
  const theme = useTheme()
  const isDownXs = useMediaQuery(theme.breakpoints.down('xs'))

  const classes = useDialogStyle()

  const scrollableContainerId = 'scrollable-container'
  const scrollableContainerSelector = `#${scrollableContainerId}`

  return (
    <ResponsiveDialog
      fullScreen={isDownXs}
      open={open}
      maxWidth="md"
      className={classesDialog.dialogStyle}
      onEscapeKeyDown={onClosingDialog}
      removeAllSpace
    >
      <DialogTitle
        id="scroll-dialog-title"
        className={clsx(classes.dialogContent, 'pt-5')}
      >
        <div className="flex justify-between flex-row items-center my-5">
          <FormattedMessage id="createVoucher.dialog.title" />
          <Icon
            className={clsx(classes.classClose, classes.iconSize)}
            onClick={onClosingDialog}
            color="action"
          >
            close
          </Icon>
        </div>
      </DialogTitle>

      <DialogContent id={scrollableContainerId}>
        <GridAllItem>
          <div className={clsx('w-full', classesDialog.dialogSubTitle)}>
            <Typography>
              <FormattedMessage id="createVoucher.dialog.description" />
            </Typography>
          </div>
        </GridAllItem>
        <Divider className="my-16" />
        <Formsy
          onValidSubmit={(model) => {
            submitVoucher(
              R.mergeRight(model, { voucherTemplateId: template.id }),
            )
          }}
          onValid={enableButton}
          onInvalid={disableButton}
          onChange={onFormChange}
          ref={formRef}
        >
          <GridAllItem>
            <Grid container className="w-full">
              <Grid item className="w-full px-32 py-5">
                <TextEditorDraftFormsy
                  name="description"
                  value={R.prop('description', initialModel)}
                  templates={R.prop('data', templateList)}
                  templateId={R.prop('templateId', initialModel)}
                  isDisabled={isDisabled}
                  onSelectTemplate={setTemplate}
                  label={formatMessage({
                    id: 'createNotification.dialog.title.label',
                  })}
                  validations={{
                    isValid: (values, value) => {
                      return htmlToTextCount(value) > 0
                    },
                  }}
                />
              </Grid>
            </Grid>
          </GridAllItem>
          <Divider className="my-16" />
          <GridAllItem className="px-32">
            <Typography className="font-bold mb-5 p-0">
              <FormattedMessage id="createNotification.dialog.voucher.instruction.header" />
            </Typography>
            <div className="mb-10">
              <RichTextEditorFormsy
                name="title"
                inputProps={{ 'data-testid': 'title' }}
                value={R.prop('title', initialModel)}
                label={formatMessage({
                  id: 'createNotification.dialog.voucher.instruction.title.label',
                })}
                required
                maxLength={TITLE_MAX_LENGTH}
                validations={{
                  textLength: richTextLengthValiator(TITLE_MAX_LENGTH),
                }}
                readOnly={isDisabled}
                linkEnabled
                isNoRichFormat
                isControlsInline={!minSize}
                customControls={minSize}
                controls={textEditorControls}
                scrollableContainer={scrollableContainerSelector}
              />
            </div>
            <div className="mb-10">
              <RichTextEditorFormsy
                name="body"
                value={R.prop('body', initialModel)}
                inputProps={{ 'data-testid': 'body' }}
                required
                label={formatMessage({
                  id: 'createNotification.dialog.voucher.instruction.description.label',
                })}
                maxLength={BODY_MAX_LENGTH}
                validations={{
                  textLength: richTextLengthValiator(BODY_MAX_LENGTH),
                }}
                readOnly={isDisabled}
                linkEnabled
                isNoRichFormat
                isControlsInline={!minSize}
                customControls={minSize}
                controls={textEditorControls}
                scrollableContainer={scrollableContainerSelector}
              />
            </div>
            <div>
              <VoucherStartEndTimeFormsy
                name={VOUCHER}
                value={R.prop('voucher', initialModel)}
                initialModel={initialModel}
                inputProps={{ 'data-testid': VOUCHER }}
                isStatusInActive={isStatusInActive}
                isEditMode={isEditMode}
                isAttached={isAttached}
                validations={{
                  isValid: (values, value) => {
                    return R.isNil(value)
                      ? false
                      : !R.isNil(value.startDateTime)
                  },
                }}
              />
            </div>
            <div>
              <Grid container className="flex items-center" spacing={1}>
                <Grid item xs={12} md={6} className="">
                  <NumberFieldFormsy
                    value={R.prop('limitedAmount', initialModel)}
                    name="limitAmount"
                    disabled={isDisabled}
                    label={formatMessage({
                      id: 'createNotificationBeacon.form.dialog.limitAmount',
                    })}
                    placeholder={formatMessage({
                      id: 'createNotificationBeacon.form.dialog.limitAmount',
                    })}
                    required
                    inputProps={{
                      'data-testid': 'limitAmount',
                    }}
                    type="number"
                    validations={{
                      isValid: (values, value) =>
                        Number(value) >= MINIMUM_LIMIT_AMOUNT &&
                        Number(value) <= MAXIMUM_LIMIT_AMOUNT,
                    }}
                    validationErrors={{
                      isValid: formatMessage(
                        {
                          id: 'createNotificationBeacon.form.dialog.limitAmount.invalid',
                        },
                        {
                          min: Number(MINIMUM_LIMIT_AMOUNT).toLocaleString(),
                          max: Number(MAXIMUM_LIMIT_AMOUNT).toLocaleString(),
                        },
                      ),
                    }}
                    min={MINIMUM_LIMIT_AMOUNT}
                    max={MAXIMUM_LIMIT_AMOUNT}
                    margin="normal"
                    variant="outlined"
                    fullWidth
                    onKeyDown={(event) => {
                      if (['e', 'E', '+', '-'].includes(event.key))
                        event.preventDefault()
                    }}
                  />
                </Grid>
              </Grid>
            </div>
          </GridAllItem>
          {isOpenPreview && (
            <PreviewVoucherFormsy
              name="previewVoucher"
              template={template.imageUrl}
              templateId={template.id}
              open={isOpenPreview}
              onClose={onClosePreview}
              vouchers={previewVoucherData}
              onClickSendNow={closePreviewThenSubmit}
              submitLabelId="action.save"
              isSubmitButtonDisabled={isFormDisable() || isStatusInActive}
            />
          )}
        </Formsy>
      </DialogContent>

      <ResponsiveDialogAction
        StartButton={
          <SecondaryButton
            className="w-full"
            labelId="createNotification.dialog.preview"
            disabled={
              isEditMode ? !isFormValid || isSubmitting : isFormDisable()
            }
            onClick={onOpenPreview}
          />
        }
        EndButton={
          <PrimaryButton
            className="w-full"
            labelId="action.save"
            isLoading={isSubmitting}
            disabled={isFormDisable() || isStatusInActive}
            onClick={callSubmit}
          />
        }
      />
      <ConfirmationDialog
        open={isClosingDialogWarning}
        title={formatMessage({
          id: 'createVoucher.dialog.confirmClose.dialog.title',
        })}
        body={
          <div className="text-black">
            {formatMessage({
              id: 'createVoucher.dialog.confirmClose.dialog.body',
            })}
          </div>
        }
        CancelButton={<CancelButton onClick={cancelClosingDialogWarning} />}
        ConfirmButton={
          <PrimaryButton onClick={closeDialog} labelId="action.ok" />
        }
      />
    </ResponsiveDialog>
  )
}

export default React.memo(CreateVoucherDialog)
